import { FieldTypes } from 'drapcode-constant';
import { extractRefField } from './field-options';
import { findComponentType } from '../../features/events/ShowHideComponentUtils';

const allowedListTableGroupTypes = [
  'data-group',
  'data-list',
  'data-table',
  'data-group-external-api',
];
const allowedComponentTypes = [
  ...allowedListTableGroupTypes,
  'bootstrap_button',
  'button',
  'header',
  'text',
  'link',
  'paragraph',
  'form',
  'login-form',
  'search-form',
  'collection-form',
  'collection-field',
  'input',
  'select',
  'navbar',
  'link-box',
  'drapcode-icons',
  'stripe-connect-buy',
  'jumbotron',
  'box',
  'iframe',
];
const extractComponents = (pageElement) => {
  const pageComponents =
    pageElement && Object.keys(pageElement).length > 0 && pageElement['nocode-components']
      ? JSON.parse(pageElement['nocode-components'])
      : [];
  return pageComponents;
};

const filterComponentByType = (pageComponents, allowedComponentTypes) => {
  if (!pageComponents || pageComponents.length === 0) {
    return [];
  }
  let filteredComponent = [];
  pageComponents.forEach((comp) => {
    if (allowedComponentTypes.includes(comp.type)) {
      filteredComponent.push(comp);
    } else {
      allowedComponentTypes.forEach((allowedComponent) => {
        filteredComponent = filteredComponent.concat(findType(allowedComponent, comp));
      });
    }
  });
  return filteredComponent;
};

export const extractPageComponents = (pages) => {
  let pageComponentList = [];
  pages.forEach((page) => {
    let options = pageComponentOptions(page);
    if (options && options.length > 0) {
      options.forEach((option) => {
        const pComp = {
          pageName: page.name,
          pageSlug: page.slug,
          pageAndComponent: page.slug + ':' + option.component,
          component: option.component,
          componentType: option.componentType,
        };
        pageComponentList.push(pComp);
      });
    }
  });
  return pageComponentList;
};

const pageComponentOptions = (page) => {
  let pageElement = page ? page.content : {};
  const pageComponents = extractComponents(pageElement);
  let filteredComponent = filterComponentByType(pageComponents, allowedComponentTypes);
  if (!filteredComponent) {
    return [];
  }
  let options = [];
  filteredComponent.forEach((filteredComp) => {
    const { attributes, type, tagName } = filteredComp;
    if (
      typeof type !== 'undefined' &&
      typeof attributes !== 'undefined' &&
      attributes['id'] !== 'undefined' &&
      attributes['id']
    ) {
      let componentDetail = { component: '', componentType: type };
      if (tagName) {
        componentDetail.component += tagName;
      } else if (type) {
        componentDetail.component += convertStringToLabel(type, '-');
      }
      if (attributes['title']) {
        componentDetail.component += `(${attributes['title']})`;
      }
      componentDetail.component += ':';
      if (attributes['id']) {
        componentDetail.component += attributes['id'];
      }
      options.push(componentDetail);
    }
  });
  return options;
};
export const pageRefreshElementOptions = (state) => {
  let pageElement = state.refreshPageContent ? state.refreshPageContent : {};
  const pageComponents = extractComponents(pageElement);
  let filteredComponent = filterComponentByType(pageComponents, allowedListTableGroupTypes);
  if (!filteredComponent) {
    return [];
  }
  let options = [];
  options = options.concat(convertComponentToOption(filteredComponent));
  return options;
};

export const getPageComponentOptions = (state, filterComponents = []) => {
  let options = [];
  let pageElement = state.refreshPageContent ? state.refreshPageContent : {};
  const pageComponents = extractComponents(pageElement);
  let filteredComponent = [];
  if (pageComponents.length > 0) {
    pageComponents.forEach((comp) => {
      if (filterComponents && filterComponents.length > 0) {
        filterComponents.forEach((filterComp) => {
          if (comp.tagName === filterComp) {
            filteredComponent.push(comp);
          } else {
            filteredComponent = filteredComponent.concat(findByTag(filterComp, comp));
          }
        });
      } else {
        filteredComponent = filteredComponent.concat(findComponentType(comp));
      }
    });
  }
  options = options.concat(convertComponentToOption(filteredComponent));
  return options;
};

export const getComponentTypeOptions = (pageComponents, componentTypes) => {
  let options = [];
  let filteredComponent = [];
  if (pageComponents.length > 0) {
    pageComponents.forEach((comp) => {
      if (componentTypes && componentTypes.length) {
        componentTypes.forEach((componentType) => {
          if (comp.type === componentType) {
            if (comp) filteredComponent.push(comp);
          } else {
            const component = findByType(componentType, comp);
            if (component) filteredComponent = filteredComponent.concat(component);
          }
        });
      } else {
        const component = findComponentType(comp);
        if (component) filteredComponent = filteredComponent.concat(component);
      }
    });
  }
  options = options.concat(convertComponentToOption(filteredComponent));
  return options.filter((opt) => opt !== undefined && typeof opt !== 'undefined');
};

const convertComponentToOption = (filteredComponent) => {
  if (!filteredComponent) {
    return [];
  }
  return filteredComponent.map((filteredComp) => {
    const { attributes, type } = filteredComp;
    if (typeof attributes !== 'undefined' && typeof type !== 'undefined' && attributes['id']) {
      const title = attributes['title']
        ? `( Name: ${attributes['title']}, ID: ${attributes['id']} )`
        : `( ID:  ${attributes['id']} )`;
      return {
        value: `${type}:${attributes['id']}`,
        label: `${synthesizeComponentTypeLabel(type)} ${title}`,
      };
    }
  });
};

const findType = (type, currComponents) => {
  const result = [];
  const find = (components) => {
    if (components) {
      components.forEach((item) => {
        if (isType(type, item)) {
          result.push(item);
        }
        find(item.components);
      });
    }
  };
  find(currComponents.components);
  return result;
};

export const findFormElementFromFormComponent = (selectedComponent) => {
  const result = [];
  const find = (components) => {
    if (components) {
      components.forEach((item) => {
        if (isType('input', item) || isType('select', item)) {
          result.push(item);
        }
        find(item.components);
      });
    }
  };
  find(selectedComponent.components);
  return result;
};

export const findByTag = (type, currComponents) => {
  const result = [];
  const find = (components) => {
    if (components) {
      components.forEach((item) => {
        if (isTagName(type, item)) {
          result.push(item);
        }
        find(item.components);
      });
    }
  };
  find(currComponents.components);
  return result;
};

export const findById = (id, pageComponents) => {
  let findItem = {};
  let i = 1;
  const find = (components) => {
    if (components) {
      components.forEach((item) => {
        if (item.attributes && item.attributes['id'] === id) {
          findItem = item;
          return;
        }
        find(item.components);
      });
    }
  };
  find(pageComponents.components);
  return findItem;
};

export const findByType = (type, currComponents) => {
  const result = [];
  const find = (components) => {
    if (components) {
      components.forEach((item) => {
        if (isType(type, item)) {
          result.push(item);
        }
        find(item.components);
      });
    }
  };
  find(currComponents.components);
  return result;
};

const isType = (type, comp) => {
  return comp.type === type;
};
const isTagName = (type, comp) => {
  return comp.tagName === type;
};

export const convertStringToLabel = (str, splitter, joiner = ' ') => {
  return str
    .toLowerCase()
    .split(splitter)
    .map((s) => (!['via', 'viz'].includes(s) ? s.charAt(0).toUpperCase() + s.substring(1) : s))
    .join(joiner);
};

export const synthesizeComponentTypeLabel = (str) => {
  let synthesizedStr = convertStringToLabel(str, '-');
  if (synthesizedStr.includes('_')) {
    synthesizedStr = convertStringToLabel(synthesizedStr, '_');
  }
  return synthesizedStr;
};
export const thirdPartyAuth = [
  'signUpUserWithFirebase',
  'signUpUserWithXano',
  'signUpUserWithBackendless',
  'loginUserWithFirebase',
  'loginUserWithXano',
  'loginUserWithBackendless',
];
const PAGE_COLLECTION = 'PAGE_COLLECTION';
const PARENT_COLLECTION = 'PARENT_COLLECTION';
const LOGGEDIN_USER_COLLECTION = 'LOGGEDIN_USER_COLLECTION';

export const pathOptions = [
  { value: PAGE_COLLECTION, label: 'Page Collection' },
  { value: PARENT_COLLECTION, label: 'Collection (Data Group/Table)', icon: 'type-bold' },
  { value: LOGGEDIN_USER_COLLECTION, label: 'LoggedIn User', icon: 'font' },
];

export const getPathCollection = (type, pageSlug, parentCollectionName, pages, collections) => {
  let collectionName = '';
  switch (type) {
    case PAGE_COLLECTION:
      pageSlug = pageSlug.replace('/', '');
      const page = pages.find((page) => page.slug === pageSlug);
      collectionName = page ? page.collectionId : '';
      break;
    case PARENT_COLLECTION:
      collectionName = parentCollectionName;
      break;
    case LOGGEDIN_USER_COLLECTION:
      collectionName = 'user';
      break;
    default:
      collectionName = '';
      break;
  }
  return collections.find((coll) => coll.collectionName === collectionName);
};

export const getPathFieldOptions = (path, fields, collections) => {
  const refFields = extractRefField(fields, collections, [FieldTypes.uuid.id]);
  fields = fields.filter((field) => field.type === FieldTypes.uuid.id);
  let fieldsOptions = [];
  let refFieldsOptions = [];
  let fieldPrefix = '';
  let refFieldPrefix = '';
  if (path === PAGE_COLLECTION) {
    fieldPrefix = 'PAGE::';
    refFieldPrefix = 'PAGE::';
  } else if (path === LOGGEDIN_USER_COLLECTION) {
    fieldPrefix = 'current_user.';
    refFieldPrefix = 'current_user.';
  } else if (path === PARENT_COLLECTION) {
    refFieldPrefix = 'RF::';
  }
  fieldsOptions = fields.map((field) => ({
    value: `{{${fieldPrefix}${field.fieldName}}}`,
    label: field.fieldTitle.en,
  }));
  refFieldsOptions = refFields.map((field) => ({
    value: `{{${refFieldPrefix}${field.fieldName}}}`,
    label: field.fieldTitle,
  }));
  return [...fieldsOptions, ...refFieldsOptions];
};

export const getPageComponents = (pageContent, componentType) => {
  let options = [];
  let pageElement = pageContent ? pageContent : {};
  const pageComponents =
    Object.keys(pageElement).length > 0 ? JSON.parse(pageElement['nocode-components']) : [];
  if (pageComponents) options = getComponentTypeOptions(pageComponents, componentType);
  return options;
};
